/**
 * The file name a response asks for in `Content-Disposition`. Prefers the RFC 5987 `filename*`
 * (which carries non-ASCII names such as a Gujarati member's) over the plain `filename`.
 * Returns null when the header is missing or has no name.
 */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // A malformed escape: fall through to the plain name.
    }
  }

  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header)?.[1];
  return plain ? plain.trim() : null;
}

/**
 * A blob as a `data:` URL, ready for an `<img src>`.
 *
 * Images behind the API's authentication cannot be fetched by the browser itself, so they are
 * fetched as a blob first. A data URL is used rather than `URL.createObjectURL`, because it is a
 * plain string: it can be cached and handed around with no object to release afterwards.
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('The image could not be read'));
    };
    reader.readAsDataURL(blob);
  });
}

/** Saves a blob under `fileName` through a temporary link. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  // After the click has handed the file to the browser.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
